import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import StatusChip, { QNA_ANSWER_STATUS_CHIP } from '../../component/StatusChip'
import { FORM_PANEL_SX, FORM_SUB_PANEL_SX } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyProductQnaDraft,
    clearProductQnaDraft,
    createEditProductQnaDraftKey,
    createProductQnaDraftPayload,
    readProductQnaDraft,
    saveProductQnaDraft,
} from './productQnaDraftUtils'

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultData = () => ({})

const getAnswerStatus = (data) => {
    if (typeof data?.status !== 'undefined' && data.status !== null) {
        return data.status
    }

    return String(data?.answer || '').trim() ? 1 : 0
}

const detailSectionSx = {
    ...FORM_PANEL_SX,
    p: 2,
    mb: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'none',
}

const detailFieldSx = {
    ...FORM_SUB_PANEL_SX,
    p: 1.25,
    minHeight: 72,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
}

const stretchMultilineTextFieldSx = {
    flex: 1,
    display: 'flex',
    '& .MuiInputBase-root': {
        flex: 1,
        alignItems: 'flex-start',
    },
    '& textarea': {
        height: '100% !important',
    },
}

const getDetailDisplayValue = (value) => {
    if (value === undefined || value === null || value === '') {
        return '-'
    }

    return value
}

const getUserId = (data) => data?.user?.userId ?? data?.userId

function DetailSection({ title, description, children, contentSx = {} }) {
    return (
        <Paper variant="outlined" sx={detailSectionSx}>
            <Box sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {title}
                </Typography>
                {description && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                        {description}
                    </Typography>
                )}
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', ...contentSx }}>
                {children}
            </Box>
        </Paper>
    )
}

function DetailField({ label, value, onClick }) {
    const clickable = typeof onClick === 'function'

    return (
        <Box
            component={clickable ? 'button' : 'div'}
            type={clickable ? 'button' : undefined}
            sx={{
                ...detailFieldSx,
                ...(clickable ? {
                    width: '100%',
                    appearance: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    textAlign: 'left',
                    font: 'inherit',
                    cursor: 'pointer',
                    '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                    },
                    '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: 2,
                    },
                } : {}),
            }}
            onClick={onClick}
            aria-label={clickable ? `${label} 복사` : undefined}
        >
            <Typography variant="caption" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 700 }}>
                {label}
            </Typography>
            {React.isValidElement(value) ? value : (
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700, wordBreak: 'break-word' }}>
                    {getDetailDisplayValue(value)}
                </Typography>
            )}
        </Box>
    )
}

const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        return
    }

    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.setAttribute('readonly', '')
    textArea.style.position = 'absolute'
    textArea.style.left = '-9999px'
    document.body.appendChild(textArea)
    textArea.select()

    const copied = document.execCommand('copy')
    document.body.removeChild(textArea)

    if (!copied) {
        throw new Error('copy_failed')
    }
}

export default function Qna(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.productQnaId
    const listCurPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchType: '', searchStatus: '' }
    const listSearch = location.search || ''
    const draftKey = createEditProductQnaDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [data, setData] = useState(() => createDefaultData())
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const initialDraftSnapshotRef = useRef('')

    const resetDetailState = useCallback((errorMessage) => {
        setData(createDefaultData())
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage(errorMessage)
    }, [])

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 상품 문의 상세를 불러올 수 없습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/productQna/getProductQna', { productQnaId: id })

                if (data.code === 1) {
                    const baseData = data.data || {}

                    if (!baseData.productQnaId) {
                        resetDetailState('상품 문의 상세 정보를 찾을 수 없습니다.')
                        return
                    }

                    const storedDraft = readProductQnaDraft(draftKey)
                    const restoredDraft = applyProductQnaDraft({
                        baseData,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createProductQnaDraftPayload({
                        data: baseData,
                    }))

                    setData(restoredDraft.data)
                    setHasDetail(Boolean(baseData?.productQnaId))
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '상품 문의 상세를 불러오지 못했습니다.'
                    resetDetailState(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '상품 문의 상세를 불러오지 못했습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }
        getData()
    }, [draftKey, history, id, resetDetailState])

    useEffect(() => {
        if (!draftReady || !hasDetail || !data?.productQnaId) {
            return
        }

        // 상품 문의 상세는 목록 query를 건드리지 않고 답변 draft만 sessionStorage에 보존한다.
        const nextDraft = createProductQnaDraftPayload({
            data,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearProductQnaDraft(draftKey)
            return
        }

        saveProductQnaDraft(draftKey, nextDraft)
    }, [data, draftKey, draftReady, hasDetail])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        // 답변 저장 후에도 운영자가 보던 상품 문의 목록 조건으로 그대로 복귀시킨다.
        clearProductQnaDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/productQnas',
            search: listSearch,
            searchText: searchText,
            searchSelect: searchSelect,
            curPage: listCurPage,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleUpdateField = (e) => {
        setData({
            ...data,
            [e.target.name]: e.target.value
        })
    }

    const handleCopyUserId = async () => {
        const copyValue = String(getUserId(data) ?? '').trim()

        if (!copyValue) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('복사할 userId 값이 없습니다.')
            return
        }

        try {
            await copyTextToClipboard(copyValue)
            setOpenSnackbar(true)
            setSeveritySnackbar('success')
            setMessageSnackbar('userId를 복사했습니다.')
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('userId를 복사하지 못했습니다.')
        }
    }

    const updateQna = async () => {
        if (saving) {
            return
        }

        const trimmedAnswer = String(data?.answer || '').trim()

        if (!trimmedAnswer) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('답변을 입력하세요.')
            return
        }

        try {
            setSaving(true)

            const { data: response } = await API.post('/api/v1/admin/productQna/update', { productQnaId: data.productQnaId, answer: trimmedAnswer })

            if (response.code === 1) {
                moveToList({ severity: 'success', message: '상품 문의 답변을 저장했습니다.' })
            } else if (response.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(response.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('상품 문의 답변을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        updateQna()
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="상품 문의 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/productQnas" search={listSearch} visible={false} searchText={searchText} searchSelect={searchSelect} curPage={listCurPage} onBackClick={() => clearProductQnaDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>상품 문의 답변</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>상품 문의 답변</Title>
                <Box component="form" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }} >
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                            <DetailSection
                                title="문의 정보"
                                description="상품과 작성자를 확인합니다."
                            >
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12}>
                                        <DetailField
                                            label="답변 상태"
                                            value={<StatusChip status={getAnswerStatus(data)} config={QNA_ANSWER_STATUS_CHIP} />}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DetailField label="상품" value={data.product?.title} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DetailField label="작성자" value={data.user?.username} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DetailField label="userId" value={getUserId(data)} onClick={handleCopyUserId} />
                                    </Grid>
                                </Grid>
                            </DetailSection>
                        </Grid>
                        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
                            <DetailSection
                                title="문의 내용"
                                description="사용자가 작성한 상품 문의 원문입니다."
                            >
                                <FormControl fullWidth sx={{ flex: 1, display: 'flex' }}>
                                    <TextField
                                        id="content"
                                        name="content"
                                        multiline
                                        minRows={8}
                                        value={data.content || ''}
                                        size="small"
                                        InputProps={{ readOnly: true }}
                                        sx={stretchMultilineTextFieldSx}
                                    />
                                </FormControl>
                            </DetailSection>
                        </Grid>
                        <Grid item xs={12}>
                            <DetailSection
                                title="답변 작성"
                                description="저장하면 상품 문의 목록으로 돌아갑니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        label="답변"
                                        id="answer"
                                        name="answer"
                                        multiline
                                        minRows={8}
                                        value={data.answer || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                        disabled={saving}
                                        placeholder="고객에게 전달할 답변을 입력하세요."
                                    />
                                </FormControl>
                            </DetailSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        path="/productQnas"
                        search={listSearch}
                        visible={true}
                        searchText={searchText}
                        searchSelect={searchSelect}
                        curPage={listCurPage}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        onBackClick={() => clearProductQnaDraft(draftKey)}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
